<template>
  <div>
    <!--
    Display the checkbox and input on one line for activate user and user activation summary
    modals (on the users aside)
    -->
    <section
      v-if="displayInline"
    >
      <div
        :class="$style.checkboxContainer"
      >
        <Checkbox
          v-model="sendWelcomeEmail"
          :class="$style.welcomeCheckbox"
          :data-test-id="$testId('sendWelcomeEmail')"
          :label="sendEmailText"
        />
        <InputText
          v-if="showEmailAddress"
          v-model="welcomeEmailAddress"
          :class="$style.emailField"
          :data-test-id="$testId('welcomeEmailAddress')"
          label=""
          name="email"
          rules="required|email"
        />
      </div>
      <ParagraphBody
        bodySize="small"
        :class="$style.infoText"
        :data-test-id="$testId('sendEmailText')"
      >
        If you specified a password, a welcome email
        will be sent requiring the user to log in with that password. Otherwise,
        an activation email will be sent allowing the user to create a password.
        <Link
          :data-test-id="$testId('learnMore')"
          :href="$options.kbLink"
          isExternal
          target="_blank"
          text="Managing User States"
        />
      </ParagraphBody>
    </section>
    <!-- Used for bulk activations -->
    <section v-else>
      <Checkbox
        v-model="sendWelcomeEmail"
        :data-test-id="$testId('sendWelcomeEmail')"
        :label="sendEmailText"
      />
      <TransitionWrapper
        name="slideVertical"
      >
        <section
          v-if="sendWelcomeEmail"
          :class="$style.welcomeEmail"
        >
          <ParagraphBody
            bodySize="small"
            :class="$style.text"
            :data-test-id="$testId('sendEmailText')"
          >
            Once activated, users with a provided password will receive a Welcome email. Those
            without a password will receive an Activation email, allowing them to create a
            password. Suspended users will always receive an Account Restored email at their
            company email address.
            <Link
              v-if="sendEmailWhenActivated"
              :data-test-id="$testId('learnMore')"
              :href="$options.kbLink"
              isExternal
              target="_blank"
              text="Managing User States"
            />
          </ParagraphBody>
          <InputText
            v-if="showEmailAddress"
            v-model="welcomeEmailAddress"
            :data-test-id="$testId('welcomeEmailAddress')"
            label="Email destination"
            name="email destination"
            rules="required|email"
          />
        </section>
      </TransitionWrapper>
    </section>
  </div>
</template>

<script>
import {
  Checkbox,
  InputText,
  Link,
  ParagraphBody,
  TransitionWrapper,
} from '@jumpcloud/ui-components';
import { mapState } from 'vuex';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import createComputedObjectBinding from 'client/util/createComputedObjectBinding';

const systemUserModel = 'SystemUserModel';

export default {
  name: 'FieldsSendEmail',

  components: {
    Checkbox,
    InputText,
    Link,
    ParagraphBody,
    TransitionWrapper,
  },

  props: {
    displayInline: Boolean,

    inMainPanel: Boolean,

    sendEmailWhenActivated: Boolean,

    value: {
      type: Object,
      default: () => {},
    },
  },

  computed: {
    ...mapState(systemUserModel, ['item']),

    sendEmailText() {
      const text = 'Send email';
      return this.sendEmailWhenActivated ? `${text} when activated` : `${text} to`;
    },

    sendWelcomeEmail: {
      ...createComputedObjectBinding('sendWelcomeEmail'),
    },

    showEmailAddress() {
      // Hide the email for schedule activation on main page
      return !(this.inMainPanel && this.sendEmailWhenActivated);
    },

    welcomeEmailAddress: {
      ...createComputedObjectBinding('welcomeEmailAddress'),
    },
  },

  kbLink: KbArticleUrls.managingUserStates,

  mounted() {
    this.setInitialAddress();
  },

  methods: {
    /**
     * Determines which email to display initially:
     *   if the user has a password (is activated)
     *     use primary email (item.email)
     *   else if the user does not have a password
     *     use secondary email if it is set (item.alternateEmail)
     *     else use primary email (item.email)
     *
     * Emits input event to set the initial value of the address
     */
    setInitialAddress() {
      const { activated, alternateEmail, email } = this.item;

      const address = activated ? email : (alternateEmail || email) || '';

      this.$emit('input', {
        ...this.value,
        welcomeEmailAddress: address,
      });
    },
  },
};
</script>

<style module scoped>
.checkboxContainer {
  display: flex;
}

.welcomeCheckbox {
  padding-right: 10px;
  padding-top: 10px;
}

.welcomeEmail {
  margin: var(--jc-spacer) 0 0 var(--jc-spacer-medium);
}

.emailField {
  --input-wrapper-width: var(--jc-size-18);
}

.infoText {
  color: var(--jc-text-color-light);
  padding-left: var(--jc-spacer);
}

.text {
  color: var(--jc-text-color-light);
}
</style>
